import type { ReactNode } from 'react';
import { Mono } from '@/components/ui/primitives';
import { DomainStatus } from '@/components/ui/status-badge';

/**
 * Renders structured results. Fields shaped as Assessed<T> (value + status + evidence) show
 * their epistemic status explicitly, so INFERRED or UNKNOWN content is never presented as fact
 * (ADR-018). Evidence is shown as structured source references, never as free text.
 */
interface Claim {
  value: unknown;
  status: string;
  evidence: { kind: string; id: string; connection?: string; quoteHash?: string; url?: string }[];
  confidence: number;
  rationale?: string;
}

function isClaim(v: unknown): v is Claim {
  return (
    !!v &&
    typeof v === 'object' &&
    !Array.isArray(v) &&
    'status' in v &&
    'evidence' in v &&
    'confidence' in v &&
    Array.isArray((v as Claim).evidence)
  );
}

const label = (k: string) =>
  k.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());

function ClaimView({ c }: { c: Claim }) {
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <DomainStatus value={c.status.toLowerCase()} />
        <span className={c.value === null ? 'text-muted italic' : ''}>
          {c.value === null
            ? 'No value'
            : typeof c.value === 'string'
              ? c.value
              : JSON.stringify(c.value)}
        </span>
        <span className="text-xs text-muted tabular-nums">
          confidence {Math.round(c.confidence * 100)}%
        </span>
      </div>
      {c.evidence.length > 0 && (
        <ul className="ml-1 space-y-0.5 text-xs text-muted">
          {c.evidence.map((e, i) => (
            <li key={i}>
              Evidence: {e.kind} <Mono>{e.id}</Mono>
              {e.connection && <> via {e.connection}</>}
              {e.quoteHash && (
                <>
                  {' '}
                  · quote <Mono title={e.quoteHash}>{e.quoteHash.slice(0, 12)}…</Mono>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {c.rationale && <p className="text-xs text-muted">{c.rationale}</p>}
    </div>
  );
}

export function StructuredValue({
  value,
  depth = 0,
}: {
  value: unknown;
  depth?: number;
}): ReactNode {
  if (isClaim(value)) return <ClaimView c={value} />;
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-muted">None</span>;
    return (
      <ul className="space-y-2">
        {(value as unknown[]).map((v, i) => (
          <li key={i} className="border-l-2 border-border pl-3">
            <StructuredValue value={v} depth={depth + 1} />
          </li>
        ))}
      </ul>
    );
  }
  if (value && typeof value === 'object') {
    if (depth > 3) return <Mono>{JSON.stringify(value)}</Mono>;
    return (
      <dl className="space-y-3">
        {Object.entries(value as Record<string, unknown>).map(([k, v]) => (
          <div key={k}>
            <dt className="text-xs font-medium text-muted">{label(k)}</dt>
            <dd className="mt-1 text-sm">
              <StructuredValue value={v} depth={depth + 1} />
            </dd>
          </div>
        ))}
      </dl>
    );
  }
  if (value === null || value === undefined) return <span className="text-muted">—</span>;
  return (
    <span className="break-words">{typeof value === 'string' ? value : JSON.stringify(value)}</span>
  );
}
